/**
 * Mise en français des manques.
 *
 * Une alerte est lue en diagonale : « il vous manque 3 postes de production et 2 postes de
 * bureaux » se comprend d'un coup d'œil, là où une liste de types suivie de parenthèses
 * demande un effort. D'où l'accord en nombre et l'énumération avec « et ».
 */

/** Noms qu'il ne faut pas accorder : sigles, ou mots déjà terminés par s, x ou z. */
function isInvariable(word) {
  if (!word) return true
  if (/[sxz]$/i.test(word)) return true
  // « EPI », « RH » : un sigle ne prend pas la marque du pluriel ici.
  return word === word.toUpperCase() && /[A-ZÀ-Ý]/.test(word)
}

/**
 * Accord en nombre d'un nom de référentiel.
 *
 * Dans un nom composé — « point de vente », « poste d'accueil » — seul le premier mot
 * s'accorde, le complément reste au singulier.
 */
export function plural(name, count) {
  const label = String(name || '').trim()
  if (count <= 1 || !label) return label

  const composed = label.match(/^(\S+)(\s+(?:d['’]|(?:de|du|des|à|a)\b).*)$/i)
  if (composed) {
    return isInvariable(composed[1]) ? label : `${composed[1]}s${composed[2]}`
  }

  return isInvariable(label) ? label : `${label}s`
}

/** Énumération française : « a, b et c ». */
export function enumerate(parts) {
  const items = parts.filter(Boolean)
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`
}

/**
 * « 3 postes de production et 2 postes de bureaux ».
 * Les locaux ne manquent pas en eux-mêmes : ce sont les postes qu'ils abritent.
 */
export function postsPhrase(counts, nameOf) {
  return enumerate(sortedEntries(counts).map(([id, count]) =>
    `${count} ${plural('poste', count)} de ${String(nameOf(id)).toLowerCase()}`))
}

/**
 * Abaisse l'initiale d'un nom commun placé en milieu de phrase.
 * Un sigle garde ses majuscules : « 1 EPI », pas « 1 ePI ».
 */
function inSentence(name) {
  const label = String(name || '')
  if (!label) return label
  const first = label.split(/\s+/)[0]
  if (isInvariable(first) && first === first.toUpperCase()) return label
  return label.charAt(0).toLowerCase() + label.slice(1)
}

/** « 3 ordinateurs, 2 téléphones et 1 EPI ». */
export function itemsPhrase(counts, nameOf) {
  return enumerate(sortedEntries(counts).map(([id, count]) =>
    `${count} ${inSentence(plural(nameOf(id), count))}`))
}

/** Énumération après une négation : « a, b ni c ». */
export function enumerateNor(parts) {
  const items = parts.filter(Boolean)
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} ni ${items[items.length - 1]}`
}

/**
 * « 2 chefs d'équipe sans téléphone ni ordinateur ; 4 jardiniers sans véhicule ».
 *
 * Les totaux par type ne suffisaient pas : un équipement sert à plusieurs métiers, et
 * « il vous manque 2 ordinateurs et 1 véhicule » laissait l'équipe deviner qui attendait
 * quoi. On regroupe donc les collaborateurs qui manquent exactement des mêmes éléments,
 * ce qui donne une ligne par situation à traiter.
 *
 * Le tour « sans X » est préféré à « n'a pas accès à un X » : il se passe du genre de
 * l'élément, que le référentiel ne déclare pas — un « une véhicule » serait pire que tout.
 *
 * @param {object[]} shortages manques par collaborateur, tels que les renvoie `consume`
 * @param {string} dimension `buildings`, `stuffs` ou `tools`
 * @param {(id: string) => string} jobName nom d'un métier
 * @param {(id: string) => string} itemName nom d'un élément
 */
export function shortageGroupsPhrase(shortages, dimension, jobName, itemName) {
  const groups = new Map()

  for (const shortage of shortages || []) {
    const items = shortage[dimension] || []
    if (!items.length) continue

    // Les doublons d'un même collaborateur — deux exigences du même type — ne comptent
    // qu'une fois : c'est bien du même élément qu'il est privé.
    const unique = Array.from(new Set(items)).sort()
    const key = `${shortage.job}|${unique.join('|')}`
    const group = groups.get(key)
    if (group) group.count += 1
    else groups.set(key, { job: shortage.job, items: unique, count: 1 })
  }

  return Array.from(groups.values())
    .sort((a, b) => b.count - a.count
      || String(jobName(a.job)).localeCompare(String(jobName(b.job))))
    .map(group => {
      // Après « sans », le nom reste au singulier quel que soit l'effectif concerné :
      // « 4 jardiniers sans véhicule ».
      const items = enumerateNor(group.items.map(id => inSentence(itemName(id))))
      // Métier comme éléments sont des noms communs en milieu de phrase : « 5 jardiniers »,
      // pas « 5 Jardiniers ». Les sigles gardent leurs majuscules.
      const who = inSentence(plural(jobName(group.job), group.count))
      return `${group.count} ${who} sans ${items}`
    })
    .join(' ; ')
}

/** Le manque le plus important d'abord. */
function sortedEntries(counts) {
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
}

export default { plural, enumerate, enumerateNor, postsPhrase, itemsPhrase, shortageGroupsPhrase }

